import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import { cn } from '../../lib/utils';
import {
  Eye, Zap, Link2, Layers, ArrowRight, Check, ChevronRight,
  Monitor, PanelRightOpen, Puzzle, ScanLine, Sparkles, Shield,
  Copy, FileText, Clock, Lock, X, Globe, Download, Smartphone, Apple, MonitorSmartphone
} from 'lucide-react';

const PHASES = [
  {
    id: 1, label: 'Assist', subtitle: 'Low Friction',
    icon: Eye, color: 'bg-emerald-600', lightColor: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    desc: 'Zero risk to your current workflow. AriaOne runs alongside your EHR as a read-only companion.',
    features: [
      { icon: Eye, text: 'Read-only — view patient data, no writes to your EHR' },
      { icon: Sparkles, text: 'AI generates suggestions for coding, billing, scheduling' },
      { icon: Copy, text: 'Copy/paste workflow — take what you need back to your EHR' },
      { icon: ScanLine, text: 'Lens uploads — scan docs from your EHR into AriaOne' },
      { icon: Shield, text: 'No risk — your existing system stays untouched' },
    ],
  },
  {
    id: 2, label: 'Semi-Automation', subtitle: 'Smart Assist',
    icon: Zap, color: 'bg-blue-600', lightColor: 'bg-blue-50 border-blue-200 text-blue-800',
    desc: 'AriaOne starts doing the heavy lifting. Pre-filled notes, suggested codes, structured outputs.',
    features: [
      { icon: FileText, text: 'Pre-fill SOAP notes from ambient capture' },
      { icon: Sparkles, text: 'AI suggests CPT/ICD codes with confidence scores' },
      { icon: Layers, text: 'Structured outputs ready to paste into your EHR' },
      { icon: Clock, text: 'Save 2-3 hours/day on documentation' },
    ],
  },
  {
    id: 3, label: 'Deep Integration', subtitle: 'Connected',
    icon: Link2, color: 'bg-violet-600', lightColor: 'bg-violet-50 border-violet-200 text-violet-800',
    desc: 'AriaOne connects to your EHR via FHIR. Partial write-back, workflow automation, bi-directional sync.',
    features: [
      { icon: Link2, text: 'FHIR-based connection to your EHR' },
      { icon: Zap, text: 'Partial write-back — push notes, codes, claims' },
      { icon: Sparkles, text: 'Workflow automation across billing & scheduling' },
      { icon: Lock, text: 'Full audit trail and HIPAA compliance' },
    ],
  },
  {
    id: 4, label: 'Full Migration', subtitle: 'Optional',
    icon: Layers, color: 'bg-slate-800', lightColor: 'bg-slate-100 border-slate-300 text-slate-800',
    desc: 'When you\'re ready, Lumina + revQ + Echo = your complete healthcare platform. No more juggling systems.',
    features: [
      { icon: Layers, text: 'Lumina + revQ + Echo as your unified platform' },
      { icon: Sparkles, text: 'Enable Healthcare native integration — full sync' },
      { icon: Shield, text: 'Single source of truth for all clinical & billing data' },
      { icon: Zap, text: 'NOT required — only when you\'re ready' },
    ],
  },
];

const WORKFLOW_OPTIONS = [
  {
    id: 'regular', label: 'Regular', tag: 'Default',
    icon: Globe, color: 'border-slate-300 bg-slate-50/50',
    tagColor: 'bg-slate-100 text-slate-700',
    desc: 'Use AriaOne as a standalone app in your browser. No EHR integration — just open the tab and go.',
    details: [
      'Open AriaOne in a regular browser tab',
      'Full-screen experience, no split views',
      'Works independently of any EHR',
      'Best for getting started or internal workflows',
    ],
    analogy: 'Like any other web app — just open and use',
  },
  {
    id: 'side-by-side', label: 'Side-by-Side Companion', tag: 'Fastest Adoption',
    icon: Monitor, color: 'border-emerald-300 bg-emerald-50/50',
    tagColor: 'bg-emerald-100 text-emerald-700',
    desc: 'Open AriaOne next to your EHR in split-screen. Auto-syncs patient context when you switch patients.',
    details: [
      'Split-screen optimized layout',
      'Auto-loads matching patient when opened in EHR',
      'Minimal clicking — context follows your workflow',
      'Works with any EHR, any browser',
    ],
    analogy: 'Like having an AI medical assistant sitting next to you',
  },
  {
    id: 'smart-popout', label: 'Smart Pop-Out', tag: 'Best UX',
    icon: PanelRightOpen, color: 'border-blue-300 bg-blue-50/50',
    tagColor: 'bg-blue-100 text-blue-700',
    desc: 'AriaOne runs as a floating panel or sidebar. Minimize, expand, or pin — always accessible.',
    details: [
      'Floating panel overlays your EHR',
      'Pin to side, minimize, or expand full-screen',
      'Persistent across your workflow',
      'Think: Notion AI panel, GitHub Copilot sidebar',
    ],
    analogy: 'Like Copilot in Microsoft apps — always there when you need it',
  },
  {
    id: 'embedded', label: 'Embedded / Native', tag: 'Best Integration',
    icon: Puzzle, color: 'border-violet-300 bg-violet-50/50',
    tagColor: 'bg-violet-100 text-violet-700',
    desc: 'Direct integration into your EHR UI via APIs or SMART on FHIR. Appears as part of your EHR.',
    details: [
      'SMART on FHIR launch from within your EHR',
      'Appears as a native tab or widget',
      'Deepest integration, seamless experience',
      'Requires EHR vendor support (Epic, Cerner, athena)',
    ],
    analogy: 'Like a built-in feature of your EHR — zero friction',
  },
];

const STORAGE_KEY = 'ariaone_adoption';
const ADOPTION_EVENT = 'ariaone_adoption_change';

export const useAdoption = () => {
  const [phase, setPhaseState] = useState(() => {
    try { return parseInt(localStorage.getItem(`${STORAGE_KEY}_phase`)) || 1; }
    catch { return 1; }
  });
  const [workflow, setWorkflowState] = useState(() => {
    try { return localStorage.getItem(`${STORAGE_KEY}_workflow`) || 'regular'; }
    catch { return 'regular'; }
  });

  const setPhase = (val) => {
    setPhaseState(val);
    localStorage.setItem(`${STORAGE_KEY}_phase`, val);
    window.dispatchEvent(new CustomEvent(ADOPTION_EVENT, { detail: { phase: val } }));
  };

  const setWorkflow = (val) => {
    setWorkflowState(val);
    localStorage.setItem(`${STORAGE_KEY}_workflow`, val);
    window.dispatchEvent(new CustomEvent(ADOPTION_EVENT, { detail: { workflow: val } }));
  };

  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.phase !== undefined) setPhaseState(e.detail.phase);
      if (e.detail?.workflow !== undefined) setWorkflowState(e.detail.workflow);
    };
    window.addEventListener(ADOPTION_EVENT, handler);
    return () => window.removeEventListener(ADOPTION_EVENT, handler);
  }, []);

  const currentPhase = PHASES.find(p => p.id === phase);
  const currentWorkflow = WORKFLOW_OPTIONS.find(w => w.id === workflow);

  return { phase, setPhase, workflow, setWorkflow, currentPhase, currentWorkflow };
};

const WORKFLOW_PREVIEWS = {
  'side-by-side': {
    title: 'Side-by-Side Companion',
    subtitle: 'Split-screen mode requires the AriaOne desktop app',
    image: 'https://static.prod-images.emergentagent.com/jobs/38ce4bf6-10cc-45be-a640-01e991bc19a1/images/4e2b197aff4b5dba7181b84a113f6d1507d38e2c1372223d984760f6fff92d23.png',
    desc: 'In Side-by-Side mode, AriaOne runs in a dedicated split-screen alongside your EHR. Patient context auto-syncs — switch a patient in your EHR and AriaOne follows.',
    color: 'emerald',
  },
  'smart-popout': {
    title: 'Smart Pop-Out',
    subtitle: 'Floating panel mode requires the AriaOne desktop app',
    image: 'https://static.prod-images.emergentagent.com/jobs/38ce4bf6-10cc-45be-a640-01e991bc19a1/images/137a4f2478e96d8787050197504ebd78227eb38b26ab06a834b8004e4c5725ad.png',
    desc: 'In Smart Pop-Out mode, AriaOne floats as a resizable panel over your EHR. Minimize, pin, or expand — it stays accessible throughout your workflow.',
    color: 'blue',
  },
};

const WorkflowPreviewDialog = ({ previewId, open, onOpenChange }) => {
  const preview = WORKFLOW_PREVIEWS[previewId];
  if (!preview) return null;

  const colorMap = {
    emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', btn: 'bg-emerald-600 hover:bg-emerald-700', badge: 'bg-emerald-100 text-emerald-700' },
    blue: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', btn: 'bg-blue-600 hover:bg-blue-700', badge: 'bg-blue-100 text-blue-700' },
  };
  const c = colorMap[preview.color] || colorMap.emerald;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl p-0 overflow-hidden" data-testid="workflow-preview-dialog" aria-describedby="workflow-preview-desc">
        <div className={cn("px-6 pt-6 pb-4", c.bg, c.border, "border-b")}>
          <DialogTitle className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <MonitorSmartphone className={cn("h-5 w-5", c.text)} />
            {preview.title}
          </DialogTitle>
          <p className="text-sm text-slate-600 mt-1">{preview.subtitle}</p>
        </div>

        <div className="p-6 space-y-5">
          {/* Preview Image */}
          <div className="rounded-xl overflow-hidden border border-slate-200 shadow-sm" data-testid="workflow-preview-image">
            <img
              src={preview.image}
              alt={`${preview.title} preview`}
              className="w-full h-auto object-cover"
            />
          </div>

          {/* Description */}
          <p id="workflow-preview-desc" className="text-sm text-slate-600 leading-relaxed">{preview.desc}</p>

          {/* Download CTA */}
          <div className="bg-slate-50 rounded-xl p-5 border border-slate-200">
            <p className="text-sm font-semibold text-slate-800 mb-1">Get the AriaOne Desktop & Mobile App</p>
            <p className="text-xs text-slate-500 mb-4">Download the app to unlock split-screen, pop-out, and native integration modes.</p>
            <div className="flex items-center gap-3">
              <Button className={cn("gap-2 text-white", c.btn)} data-testid="download-desktop-btn">
                <Download className="h-4 w-4" /> Download for Desktop
              </Button>
              <Button variant="outline" className="gap-2" data-testid="download-mobile-btn">
                <Smartphone className="h-4 w-4" /> Mobile App
              </Button>
            </div>
            <p className="text-[10px] text-slate-400 mt-3">Available for macOS, Windows, iOS, and Android. Coming soon.</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const AdoptionJourney = ({ app = 'lumina' }) => {
  const { phase, setPhase, workflow, setWorkflow, currentPhase } = useAdoption();
  const [expandedPhase, setExpandedPhase] = useState(null);
  const [previewMode, setPreviewMode] = useState(null);

  return (
    <div className="space-y-6" data-testid="adoption-journey">
      {/* Phase Timeline */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg font-semibold">Your Adoption Journey</CardTitle>
              <p className="text-xs text-muted-foreground mt-1">Start simple. Adopt at your pace. No lock-in, no pressure.</p>
            </div>
            <Badge className={cn("text-xs gap-1", currentPhase.lightColor)} data-testid="current-phase-badge">
              <currentPhase.icon className="h-3 w-3" /> {currentPhase.label}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          {/* Timeline */}
          <div className="flex items-center gap-0 mb-5">
            {PHASES.map((p, i) => {
              const Icon = p.icon;
              const isActive = p.id === phase;
              const isPast = p.id < phase;
              return (
                <div key={p.id} className="flex items-center flex-1">
                  <button
                    onClick={() => setPhase(p.id)}
                    className={cn(
                      "flex items-center gap-2 px-3 py-2 rounded-lg transition-all w-full",
                      isActive ? "bg-slate-900 text-white" : isPast ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100" : "bg-slate-50 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                    )}
                    data-testid={`phase-btn-${p.id}`}
                  >
                    <div className={cn("h-6 w-6 rounded-full flex items-center justify-center shrink-0", isActive ? "bg-white/20" : isPast ? "bg-emerald-200" : "bg-slate-200")}>
                      {isPast ? <Check className="h-3 w-3" /> : <Icon className="h-3 w-3" />}
                    </div>
                    <div className="text-left min-w-0">
                      <p className="text-[10px] font-bold leading-none">{p.label}</p>
                      <p className={cn("text-[9px] mt-0.5", isActive ? "text-white/70" : "opacity-60")}>{p.subtitle}</p>
                    </div>
                  </button>
                  {i < PHASES.length - 1 && <ArrowRight className={cn("h-3 w-3 shrink-0 mx-1", p.id < phase ? "text-emerald-400" : "text-slate-300")} />}
                </div>
              );
            })}
          </div>

          {/* Active Phase Detail */}
          <div className={cn("p-4 rounded-xl border", currentPhase.lightColor)} data-testid="phase-detail">
            <p className="text-sm font-semibold mb-1">{currentPhase.label}: {currentPhase.subtitle}</p>
            <p className="text-xs text-muted-foreground mb-3">{currentPhase.desc}</p>
            <div className="grid grid-cols-2 gap-2">
              {currentPhase.features.map((f, i) => {
                const FIcon = f.icon;
                return (
                  <div key={i} className="flex items-start gap-2 text-xs" data-testid={`phase-feature-${i}`}>
                    <FIcon className="h-3.5 w-3.5 mt-0.5 shrink-0 opacity-60" />
                    <span>{f.text}</span>
                  </div>
                );
              })}
            </div>
            {phase < 4 && (
              <Button variant="outline" size="sm" className="mt-3 text-xs gap-1" onClick={() => setPhase(phase + 1)} data-testid="advance-phase-btn">
                Explore {PHASES[phase]?.label || 'Next'} <ChevronRight className="h-3 w-3" />
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Workflow Options */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-semibold">How Would You Like to Use AriaOne?</CardTitle>
          <p className="text-xs text-muted-foreground">Choose your preferred workflow mode. You can change this anytime.</p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-3">
            {WORKFLOW_OPTIONS.map((opt) => {
              const Icon = opt.icon;
              const isSelected = workflow === opt.id;
              const hasPreview = opt.id === 'side-by-side' || opt.id === 'smart-popout';
              return (
                <button
                  key={opt.id}
                  onClick={() => {
                    if (hasPreview) {
                      setPreviewMode(opt.id);
                    } else {
                      setWorkflow(opt.id);
                    }
                  }}
                  className={cn(
                    "p-4 rounded-xl border-2 text-left transition-all",
                    isSelected ? "border-slate-900 bg-slate-50 shadow-md" : `${opt.color} hover:shadow-sm`
                  )}
                  data-testid={`workflow-opt-${opt.id}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <Icon className={cn("h-5 w-5", isSelected ? "text-slate-900" : "text-slate-400")} />
                    <div className="flex items-center gap-1.5">
                      {hasPreview && <Badge className="bg-slate-200 text-slate-600 text-[8px] border-0">Desktop App</Badge>}
                      <Badge className={cn("text-[9px]", opt.tagColor)}>{opt.tag}</Badge>
                    </div>
                  </div>
                  <p className="text-sm font-semibold text-slate-800">{opt.label}</p>
                  <p className="text-[10px] text-muted-foreground mt-1 leading-relaxed">{opt.desc}</p>
                  <div className="mt-2.5 space-y-1">
                    {opt.details.map((d, i) => (
                      <p key={i} className="text-[9px] text-slate-500 flex items-start gap-1">
                        <Check className={cn("h-2.5 w-2.5 mt-0.5 shrink-0", isSelected ? "text-emerald-500" : "text-slate-300")} />
                        {d}
                      </p>
                    ))}
                  </div>
                  <p className={cn("text-[9px] mt-2 italic", isSelected ? "text-slate-600" : "text-slate-400")}>{opt.analogy}</p>
                  {isSelected && (
                    <div className="mt-2 flex items-center gap-1 text-[10px] font-medium text-emerald-600">
                      <Check className="h-3 w-3" /> Active Mode
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Workflow Preview Dialog */}
      <WorkflowPreviewDialog
        previewId={previewMode}
        open={!!previewMode}
        onOpenChange={(open) => !open && setPreviewMode(null)}
      />
    </div>
  );
};

export { PHASES, WORKFLOW_OPTIONS };

const AdoptionLightbox = ({ open, onOpenChange }) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto p-0" data-testid="adoption-lightbox">
        <div className="p-6">
          <DialogTitle className="sr-only">Adoption Journey</DialogTitle>
          <AdoptionJourney />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export { AdoptionLightbox };
export default AdoptionJourney;
